/*
 * Copyright 2026 Sony Corporation
 */

import type { RealtimeEvent } from '@ips-device-verse/twin-service/schema/realtime';

import type { AgentOp, AgentTurn, RequestOps } from './agent';
import type { RealtimeClient } from './realtime-client';

/**
 * The real agent, behind U3's interface (devplan §12).
 *
 * Nothing above this file changes when the stub is swapped out: the panel still
 * calls `requestOps(prompt, digest)` and still gets text and ops back. What it
 * gains is streaming — a turn can take twenty seconds, and a chat that shows
 * nothing for twenty seconds looks broken — so the text is reported as it
 * arrives through `onDelta` and returned whole at the end.
 */

export interface AgentClientOptions {
  socket: Pick<RealtimeClient, 'send'>;
  /** Subscribes to the socket's frames; returns an unsubscribe. */
  subscribe: (listener: (event: RealtimeEvent) => void) => () => void;
  /** Called with the text so far, as it is written. */
  onDelta?: (text: string) => void;
  /** Client-generated so the reply can be matched to the question it answers. */
  newId?: () => string;
}

export class AgentUnavailable extends Error {}

export interface AgentClient<Op extends AgentOp, Digest> {
  requestOps: RequestOps<Op, Digest>;
  /** Starts a new conversation; the service keeps one transcript per thread id. */
  newThread: () => void;
}

/**
 * The turn is over when `agent_done` arrives, whatever else did.
 *
 * A turn that produced no ops is a perfectly good turn — a question answered,
 * or a refusal — so the ops are whatever `agent_ops` carried, and nothing is
 * waited for beyond the end.
 */
export const createAgentClient = <Op extends AgentOp, Digest>({
  socket,
  subscribe,
  onDelta,
  newId = () => crypto.randomUUID(),
}: AgentClientOptions): AgentClient<Op, Digest> => {
  let threadId = newId();

  const requestOps: RequestOps<Op, Digest> = (prompt, digest) =>
    new Promise<AgentTurn<Op>>((resolve, reject) => {
      const turnId = newId();
      let text = '';
      // One vocabulary per socket, decided by what it was opened for, so the
      // ops of a turn are always the ones this page knows how to apply.
      let ops: Op[] = [];

      const unsubscribe = subscribe((event) => {
        if (!('turn_id' in event) || event.turn_id !== turnId) {
          return;
        }

        if (event.type === 'agent_delta') {
          text += event.text;
          onDelta?.(text);
          return;
        }
        if (event.type === 'agent_ops') {
          ops = [...event.ops] as Op[];
          return;
        }
        if (event.type === 'agent_error') {
          unsubscribe();
          reject(new AgentUnavailable(event.message));
          return;
        }
        if (event.type === 'agent_done') {
          unsubscribe();
          resolve({ text, ops });
        }
      });

      const sent = socket.send({
        type: 'agent_prompt',
        thread_id: threadId,
        turn_id: turnId,
        text: prompt,
        digest: digest as never,
      });

      if (!sent) {
        unsubscribe();
        reject(new AgentUnavailable('The assistant is not connected. Check your network and try again.'));
      }
    });

  return {
    requestOps,
    newThread: () => {
      threadId = newId();
    },
  };
};
